/**
 * LCS 기반 줄 단위 diff + CodeMirror 변경 거터 마커 + 삭제 줄 표시
 *
 * 단순 인덱스 비교 대신 LCS(최장 공통 부분 수열)를 사용하여
 * 줄 삽입/삭제 시에도 정확한 변경 표시를 제공합니다.
 *
 * 삭제된 줄은 해당 위치 바로 위 줄에 빨간 점선 하단 테두리로 표시합니다.
 */
import { gutter, GutterMarker, Decoration, EditorView } from '@codemirror/view'
import { StateField, StateEffect, RangeSet } from '@codemirror/state'

// ── 마커 클래스 ──────────────────────────────────────────────────────────────
class ChangedMarker extends GutterMarker {
  constructor(type) { super(); this.type = type }
  toDOM() {
    const el = document.createElement('div')
    el.className = `change-marker change-${this.type}`
    return el
  }
}

const markerModified = new ChangedMarker('modified')
const markerAdded    = new ChangedMarker('added')

// ── 삭제 줄 라인 데코레이션 ─────────────────────────────────────────────────
const deletedLineDeco = Decoration.line({ class: 'cm-deleted-below' })

// ── StateEffect / StateField ─────────────────────────────────────────────────
export const setChangeMarkers = StateEffect.define()
export const setDeleteDecos = StateEffect.define()

export const changeMarkerField = StateField.define({
  create() { return RangeSet.empty },
  update(markers, tr) {
    for (const e of tr.effects) {
      if (e.is(setChangeMarkers)) return e.value
    }
    return tr.docChanged ? markers.map(tr.changes) : markers
  },
})

export const deleteDecoField = StateField.define({
  create() { return Decoration.none },
  update(decos, tr) {
    for (const e of tr.effects) {
      if (e.is(setDeleteDecos)) return e.value
    }
    return tr.docChanged ? decos.map(tr.changes) : decos
  },
  provide: (f) => EditorView.decorations.from(f),
})

export const changeGutter = gutter({
  class: 'cm-change-gutter',
  markers: (view) => view.state.field(changeMarkerField, false) ?? RangeSet.empty,
})

// ── LCS 기반 diff ────────────────────────────────────────────────────────────

/**
 * 두 문자열 배열 간 LCS 편집 스크립트를 계산합니다.
 * 각 현재 줄에 'same' | 'modified' | 'added' 상태를 부여하고,
 * 삭제가 발생한 위치(현재 문서 기준 줄 인덱스)를 반환합니다.
 *
 * 대형 파일(n*m > 300,000)은 단순 인덱스 비교로 폴백합니다.
 */
function diffLines(orig, curr) {
  const n = orig.length
  const m = curr.length

  // 대형 파일 폴백
  if (n * m > 300000) return null

  // LCS 테이블 구축
  const dp = []
  for (let i = 0; i <= n; i++) {
    dp[i] = new Uint16Array(m + 1)
  }
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] = orig[i - 1] === curr[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }

  // 백트래킹: 편집 스크립트 생성
  const ops = [] // { type: 'keep'|'delete'|'insert', currIdx }
  let i = n, j = m
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && orig[i - 1] === curr[j - 1]) {
      ops.push({ type: 'keep', currIdx: j - 1 })
      i--; j--
    } else if (i > 0 && (j === 0 || dp[i - 1][j] >= dp[i][j - 1])) {
      ops.push({ type: 'delete' })
      i--
    } else {
      ops.push({ type: 'insert', currIdx: j - 1 })
      j--
    }
  }
  ops.reverse()

  // 결과 배열: 각 현재 줄의 상태
  const status = new Array(m).fill('added')
  for (const op of ops) {
    if (op.type === 'keep') status[op.currIdx] = 'same'
  }

  // 연속된 비-keep 블록을 묶어서 delete + insert 쌍을 'modified'로 변환
  // 동시에 삭제 위치를 추적
  const deleteAfter = new Set() // 삭제가 발생한 직전 현재 줄 인덱스
  let k = 0
  while (k < ops.length) {
    if (ops[k].type === 'keep') { k++; continue }

    // 이 변경 블록 직전의 현재 줄 인덱스 찾기
    let lastCurrIdxBefore = -1
    for (let b = k - 1; b >= 0; b--) {
      if (ops[b].currIdx !== undefined) { lastCurrIdxBefore = ops[b].currIdx; break }
    }

    // 이 변경 블록의 delete 수와 insert 인덱스를 수집
    let deletes = 0
    const inserts = []
    while (k < ops.length && ops[k].type !== 'keep') {
      if (ops[k].type === 'delete') deletes++
      else if (ops[k].type === 'insert') inserts.push(ops[k].currIdx)
      k++
    }

    // delete와 insert를 짝지어 'modified'로 변환
    const pairs = Math.min(deletes, inserts.length)
    for (let p = 0; p < pairs; p++) {
      status[inserts[p]] = 'modified'
    }

    // 짝지어지지 않은 delete가 있으면 삭제 위치 기록
    const unpairedDeletes = deletes - pairs
    if (unpairedDeletes > 0) {
      if (inserts.length > 0) {
        // insert가 있는 블록: 마지막 insert 줄 아래에 삭제 표시
        deleteAfter.add(inserts[inserts.length - 1])
      } else {
        // insert 없이 delete만: 블록 직전 줄 아래에 삭제 표시
        deleteAfter.add(lastCurrIdxBefore)
      }
    }
  }

  return { status, deleteAfter }
}

// ── 변경 마커 생성 ───────────────────────────────────────────────────────────

/**
 * 원본과 현재 문서를 비교하여 CodeMirror 거터 마커와 삭제 줄 데코레이션을 생성합니다.
 * { gutterMarkers: RangeSet, deleteDecos: DecorationSet } 반환
 */
export function computeChangeMarkers(doc, originalLines) {
  const currentLines = []
  for (let i = 1; i <= doc.lines; i++) {
    currentLines.push(doc.line(i).text)
  }

  const diff = diffLines(originalLines, currentLines)

  if (diff) {
    // LCS diff 사용
    const builders = []
    const decoBuilders = []

    for (let i = 0; i < currentLines.length; i++) {
      const line = doc.line(i + 1)
      if (diff.status[i] === 'modified') builders.push(markerModified.range(line.from))
      else if (diff.status[i] === 'added') builders.push(markerAdded.range(line.from))
    }

    // 삭제 위치에 라인 데코레이션 추가
    for (const idx of diff.deleteAfter) {
      if (idx >= 0 && idx < currentLines.length) {
        const line = doc.line(idx + 1)
        decoBuilders.push(deletedLineDeco.range(line.from))
      } else if (idx === -1 && doc.lines > 0) {
        // 문서 맨 앞에서 삭제: 첫 줄에 상단 표시 (top border 별도 클래스)
        const line = doc.line(1)
        decoBuilders.push(Decoration.line({ class: 'cm-deleted-above' }).range(line.from))
      }
    }

    return {
      gutterMarkers: RangeSet.of(builders, true),
      deleteDecos: Decoration.set(decoBuilders, true),
    }
  }

  // 폴백: 단순 인덱스 비교
  const builders = []
  for (let i = 1; i <= doc.lines; i++) {
    const line = doc.line(i)
    if (i <= originalLines.length) {
      if (line.text !== originalLines[i - 1]) builders.push(markerModified.range(line.from))
    } else {
      builders.push(markerAdded.range(line.from))
    }
  }
  const decoBuilders = []
  if (originalLines.length > doc.lines && doc.lines > 0) {
    const line = doc.line(doc.lines)
    decoBuilders.push(deletedLineDeco.range(line.from))
  }
  return {
    gutterMarkers: RangeSet.of(builders, true),
    deleteDecos: Decoration.set(decoBuilders, true),
  }
}
